/**
 * useTickerBanners - Custom Hook
 *
 * Obtiene la configuración de banners publicitarios del ticker mediante el
 * servicio getTickerBanners y devuelve la lista de reproducción aplanada y
 * aleatorizada (todos los items de imagen de todos los banners).
 *
 * @param {Object} [options]
 * @param {number} [options.pollInterval] - Intervalo de refresco en ms.
 * @param {boolean} [options.enablePolling] - Activar refresco automático.
 * @returns {{
 *   playlist: import("../components/tickerClarosports/TickerExpandedImage/tickerBannerUtils").BannerImageItem[],
 *   loading: boolean,
 *   error: string|null,
 *   refetch: Function
 * }}
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getTickerBanners } from '../services/tickerBanners';
import { buildBannerPlaylist, getBannersRefreshMs } from '../components/tickerClarosports/TickerExpandedImage/tickerBannerUtils';

export const useTickerBanners = (options = {}) => {
  const {
    pollInterval = 0,
    enablePolling = false,
  } = options;

  const [playlist, setPlaylist] = useState([]);
  // Intervalo de recarga (ms) derivado de `time_refresh` (minutos) del servicio.
  // null si el servicio no lo define.
  const [timeRefreshMs, setTimeRefreshMs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pollIntervalRef = useRef(null);

  const fetchBanners = useCallback(async () => {
    try {
      setError(null);
      const response = await getTickerBanners();

      if (response.statusCode === 200) {
        setPlaylist(buildBannerPlaylist(response.data));
        setTimeRefreshMs(getBannersRefreshMs(response.data));
      } else {
        setError(response.message || 'Error al obtener los banners del ticker');
      }
    } catch (err) {
      console.error('Error fetching ticker banners:', err);
      setError('Error al obtener los banners del ticker');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();

    if (enablePolling && pollInterval > 0) {
      pollIntervalRef.current = setInterval(fetchBanners, pollInterval);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchBanners, pollInterval, enablePolling]);

  const refetch = useCallback(async () => {
    await fetchBanners();
  }, [fetchBanners]);

  return { playlist, timeRefreshMs, loading, error, refetch };
};
